import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { useEffect } from 'react';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">
              文章未找到
            </h1>
            <Link
              to="/"
              className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryArticles = articles.filter(
    (a) => a.category === article.category && a.slug !== article.slug
  );
  const relatedArticles = categoryArticles.slice(0, 3);
  if (relatedArticles.length < 3) {
    const moreArticles = articles
      .filter((a) => a.slug !== article.slug && a.category !== article.category)
      .slice(0, 3 - relatedArticles.length);
    relatedArticles.push(...moreArticles);
  }

  const category = categories.find((c) => c.slug === article.category);

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderContent = (content: string) => {
    const lines = content.split('\n');
    const elements: React.ReactNode[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      if (line.startsWith('## ')) {
        elements.push(
          <h2 key={i} className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-8 mb-4">
            {line.replace('## ', '')}
          </h2>
        );
        i++;
      } else if (line.startsWith('### ')) {
        elements.push(
          <h3 key={i} className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mt-6 mb-3">
            {line.replace('### ', '')}
          </h3>
        );
        i++;
      } else if (line.startsWith('**') && line.endsWith('**')) {
        elements.push(
          <p key={i} className="text-sm md:text-base font-bold text-foreground-900 mb-1 mt-3">
            {line.replace(/\*\*/g, '')}
          </p>
        );
        i++;
      } else if (line.startsWith('- ')) {
        const listItems: string[] = [];
        while (i < lines.length && lines[i].startsWith('- ')) {
          listItems.push(lines[i].replace('- ', ''));
          i++;
        }
        elements.push(
          <ul key={i} className="list-disc pl-5 mb-4 space-y-1">
            {listItems.map((item, idx) => (
              <li key={idx} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                {item}
              </li>
            ))}
          </ul>
        );
      } else if (line.startsWith('|')) {
        const tableRows: string[][] = [];
        while (i < lines.length && lines[i].startsWith('|')) {
          const cells = lines[i]
            .split('|')
            .filter((c) => c.trim() !== '')
            .map((c) => c.trim());
          tableRows.push(cells);
          i++;
        }
        if (tableRows.length >= 2) {
          elements.push(
            <div key={i} className="overflow-x-auto mb-4">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-background-100">
                    {tableRows[0].map((header, idx) => (
                      <th key={idx} className="border border-background-200 px-3 py-2 text-left font-semibold text-foreground-900">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableRows.slice(2).map((row, rowIdx) => (
                    <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-background-50' : 'bg-background-100'}>
                      {row.map((cell, cellIdx) => (
                        <td key={cellIdx} className="border border-background-200 px-3 py-2 text-foreground-700">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
      } else if (line.trim() === '') {
        i++;
      } else {
        elements.push(
          <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
            {line}
          </p>
        );
        i++;
      }
    }
    return elements;
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.summary,
            datePublished: article.date,
            url: `https://www.baoyushipin.com/article/${article.slug}`,
            author: {
              '@type': 'Organization',
              name: '鲍鱼视频',
            },
            publisher: {
              '@type': 'Organization',
              name: '鲍鱼视频',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.baoyushipin.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: article.categoryName,
                item: `https://www.baoyushipin.com/category/${article.category}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
              },
            ],
          }),
        }}
      />
      <Navbar />
      <main className="pt-16">
        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: article.categoryName, to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <header className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">
                {article.categoryName}
              </span>
              <span className="text-xs text-foreground-500">{article.date}</span>
              <span className="text-xs text-foreground-400">·</span>
              <span className="text-xs text-foreground-500 flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views.toLocaleString()} 次观看
              </span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              {article.summary}
            </p>
          </header>

          <div className="relative aspect-video rounded-xl overflow-hidden mb-8">
            <img
              src={article.thumbnail}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
              <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 backdrop-blur-sm cursor-pointer hover:bg-primary-600 transition-colors">
                <i className="ri-play-fill text-3xl md:text-4xl text-background-50 ml-1"></i>
              </div>
            </div>
            <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-sm text-background-50 text-sm px-3 py-1.5 rounded-lg flex items-center gap-2">
              <i className="ri-time-line"></i>
              {article.duration}
            </div>
          </div>

          <div className="prose-custom">
            {renderContent(article.content)}
          </div>

          {relatedArticles.length > 0 && (
            <section className="mt-12 pt-8 border-t border-background-200">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    onClick={handleClick}
                    className="group bg-background-100 rounded-xl overflow-hidden cursor-pointer border border-background-200/60 hover:border-primary-200 transition-all"
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={related.thumbnail}
                        alt={related.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute bottom-2 right-2 bg-black/70 text-background-50 text-xs px-1.5 py-0.5 rounded flex items-center gap-0.5">
                        <i className="ri-play-fill text-xs"></i>
                        {related.duration}
                      </div>
                    </div>
                    <div className="p-3">
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">
                        {related.categoryName}
                      </span>
                      <h3 className="font-heading text-sm font-semibold text-foreground-950 mt-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                        {related.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}